/**
 * ChatBody - Main content area of the chat interface
 * 
 * Handles:
 * - Welcome screen (when no messages)
 * - Message list with ChatMessages
 * - Chat input area
 * - Auto-scrolling
 */

import classNames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../../../i18n/namespace";
import chatStyles from "./ChatContainer.module.scss";
import ChatMessages from "./ChatMessages";
import WelcomeScreen from "./WelcomeScreen";
// import { CONNECTION_STATES } from "../constants";

const ChatBody = React.memo(({ chatState }) => {
    const { t } = useTranslation(i18nNamespace);
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);

    const {
        uiMessages = [],
        hasMessages,
        isStreaming,
        // connectionState,
        sendMessage
    } = chatState;

    // uiMessages already includes step indicator when needed (from useMessages hook)
    const descriptors = uiMessages;

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [descriptors]);

    // Handle suggested prompt click
    const handleSuggestedPromptClick = useCallback((text) => {
        sendMessage(text);
    }, [sendMessage]);

    // Handle component actions (from catalog components)
    const handleAction = useCallback((actionType, payload) => {
        console.log('Chat action:', actionType, payload);

        switch (actionType) {
            case 'prompt_click':
                sendMessage(payload.text);
                break;
            case 'copy':
                if (payload?.content) {
                    navigator.clipboard.writeText(payload.content);
                }
                break;
            case 'retry':
                chatState.retryLastMessage?.();
                break;
            default:
                console.log('Unhandled action:', actionType);
        }
    }, [sendMessage, chatState]);

    // Determine if input should be disabled
    // TODO: Re-enable when backend is connected
    // const isInputDisabled = isStreaming || connectionState !== CONNECTION_STATES.CONNECTED;
    const isInputDisabled = false; // Always enabled for now (no backend)

    // Placeholder text based on state
    const inputPlaceholder = isStreaming
        ? t("ai_thinking", "Assistant is thinking...")
        : undefined;

    return (
        <div className={classNames(chatStyles['menu-content'], {
            [chatStyles['chat-active']]: hasMessages
        })}>
            <div
                className={chatStyles['messages-container']}
                ref={containerRef}
            >
                {!hasMessages ? (
                    <WelcomeScreen
                        sendMessage={sendMessage}
                        isStreaming={isStreaming}
                        // connectionState={connectionState} 
                        onSuggestedPromptClick={handleSuggestedPromptClick}
                    />
                ) : (
                    <ChatMessages
                        descriptors={descriptors}
                        onAction={handleAction}
                        className={chatStyles['messages-list']}
                    />
                )}
                <div ref={messagesEndRef} />
            </div>
        </div>
    );
});

ChatBody.displayName = 'ChatBody';

export default ChatBody;